<template>
  <modal-vue
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    @close="handleClose"
    :max-width="currentStep === STEP.PREVIEW ? 'max-w-[1000px]' : 'max-w-[500px]'"
    class="register-upload-modal"
  >
    <template #header>
      <div class="flex items-center justify-between w-full">
        <h2 class="text-xl font-bold text-textPrimary">{{ stepTitle }}</h2>
      </div>
    </template>

    <!--
      M2/§8.2 — quando o wizard é reaberto pelo reencaminhamento do "Enviar registro", os
      campos que vêm da vaga chegam preenchidos (`initialForm`) e bloqueados (`lockedFields`).
      Sem as duas props nada muda: o wizard de folha é exatamente o de hoje.
    -->
    <StepForm
      v-if="currentStep === STEP.FORM"
      :model-value="form"
      :locked-fields="lockedFields"
      @update:model-value="(val) => Object.assign(form, val)"
      v-model:valid="isStepFormValid"
    />

    <StepFile v-else-if="currentStep === STEP.FILE" v-model="selectedFile" />

    <StepPreview
      v-else-if="currentStep === STEP.PREVIEW"
      :file="selectedFile"
      v-model:rotation="rotation"
      v-model:zoom="zoomScale"
      :monitoring-type-label="form.monitoringTypeLabel ?? ''"
      @update:total-pages="attachedPages = $event"
    />

    <!--
      Eixo 2A da discovery 2026-09-11 — checklist antes do POST, só nas modalidades de folha.
      `INCOMPLETE_SHEET` é a rejeição mais comum em PROD e cada caso custa um ciclo de ida e
      volta com a unidade. Nenhum item é obrigatório: o botão é "Enviar assim mesmo".
    -->
    <StepChecklist
      v-else-if="currentStep === STEP.CHECKLIST"
      v-model="selfCheckState"
      :formatted-date="formattedReferenceDate"
      :health-unit-id="healthUnitIdOrNull"
      :schedule-ids="selectedScheduleIdsAsNumbers"
      :reference-date="formatDateToISO(form.date)"
      :pages="attachedPages"
    />

    <!-- Eixo 2B — avisos da pré-validação de imagem, DEPOIS do envio. Nunca bloqueiam. -->
    <StepWarnings v-else-if="currentStep === STEP.WARNINGS" :warnings="qualityWarnings" />

    <template #footer>
      <div class="flex items-center justify-between w-full mt-2">
        <outlined-button
          data-testid="upload-modal-voltar-button"
          :text="backButtonText"
          width="160px"
          @click="handleBackAction"
          class="!border-primary30 !text-primary30 !font-bold"
        />

        <filled-button
          data-testid="upload-modal-confirmar-button"
          :text="mainButtonText"
          width="180px"
          @click="handleMainAction"
          :loading="isSaving"
          :disabled="
            (currentStep === STEP.FORM && !isStepFormValid) ||
            (currentStep === STEP.FILE && !selectedFile)
          "
          class="!bg-primary !text-white !font-bold hover:!bg-primary40"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { ImageState, DSAttendanceMonitoringType } from '@/constants/ds-attendance.constants'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import Network from '@/Network'
import type { DsAttendanceUploadForm } from './types'
import type {
  DSAttendanceUploadExtras,
  ScheduleShiftsDto
} from '@/Network/Types/Requests/DsAttendanceRequest'
import type { DSAttendanceUploadResponse } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import StepForm from './StepForm.vue'
import StepFile from './StepFile.vue'
import StepPreview from './StepPreview.vue'
import StepChecklist from './StepChecklist.vue'
import StepWarnings from './StepWarnings.vue'
import { buildSelfCheckPayload, emptySelfCheckState } from './self-check'

interface Props {
  modelValue: boolean
  /**
   * Campos que chegam da vaga de origem (M2, §8.2). O modal os usa como semente
   * do formulário a cada abertura; ausente, o wizard nasce vazio como sempre.
   */
  initialForm?: Partial<DsAttendanceUploadForm> | null
  /** Quais desses campos ficam bloqueados no passo 1. Vazio = tudo editável. */
  lockedFields?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  initialForm: null,
  lockedFields: () => []
})
const emit = defineEmits(['update:modelValue', 'save'])

/**
 * Passos nomeados: o fluxo passou de 3 para 5 com a qualidade na origem (Eixo 2 da discovery
 * 2026-09-11), e `currentStep === 3` espalhado pelo template já não dizia qual passo era.
 * CHECKLIST só existe nas modalidades de folha; WARNINGS só quando o backend devolve avisos.
 */
const STEP = {
  FORM: 1,
  FILE: 2,
  PREVIEW: 3,
  CHECKLIST: 4,
  WARNINGS: 5
} as const

const currentStep = ref<number>(STEP.FORM)
const isSaving = ref(false)

const selectedFile = ref<File | null>(null)
const rotation = ref(0)
const zoomScale = ref(1)

/** Páginas anexadas — 1 para imagem, total do PDF quando o renderizador as conta. */
const attachedPages = ref(1)
const selfCheckState = ref(emptySelfCheckState())
const qualityWarnings = ref<string[]>([])

const shiftStore = useShiftStore()
const { listShifts } = storeToRefs(shiftStore)

const getDefaultForm = (): DsAttendanceUploadForm => ({
  uploadedBy: null,
  company: null,
  healthUnit: null,
  schedules: [],
  shifts: [],
  date: null,
  imageState: ImageState.PENDING,
  uploadAnother: false,
  monitoringType: '',
  monitoringTypeLabel: ''
})

/**
 * Semente do formulário: o default de sempre, sobrescrito pelo que a vaga já
 * sabe. Cópia por spread — `initialForm` é do chamador e não pode ser mutado
 * pelos campos que o emissor ainda vai preencher.
 *
 * `company`/`healthUnit` chegam como número (id da API) de uns chamadores e como
 * string (value das stores) de outros. É de propósito: a tolerância vive no
 * `value-comparator` dos selects do `StepForm`, não numa coerção aqui.
 */
const buildInitialForm = (): DsAttendanceUploadForm => ({
  ...getDefaultForm(),
  ...(props.initialForm ?? {})
})

const form = ref<DsAttendanceUploadForm>(buildInitialForm())

const handleClose = () => {
  emit('update:modelValue', false)
  setTimeout(resetModal, 300)
}

const resetModal = () => {
  currentStep.value = STEP.FORM
  selectedFile.value = null
  rotation.value = 0
  zoomScale.value = 1
  attachedPages.value = 1
  selfCheckState.value = emptySelfCheckState()
  qualityWarnings.value = []
  form.value = buildInitialForm()
}

/**
 * Modalidades de folha física: são as que têm páginas para conferir e as que produzem
 * `INCOMPLETE_SHEET`. App externo e app Minha Escala não passam pelo checklist.
 */
/**
 * Checklist do emissor só na FOLHA diária: `POST /upload` é o único endpoint que aceita
 * `selfCheck`/`expectedPages`/`qualityWarnings`. Numa folha não diária o passo coletaria uma
 * declaração que o backend descarta.
 *
 * Fase 3: estender `upload-non-daily` com os mesmos campos e devolver `NON_DAILY_SHEET` aqui.
 */
const needsSelfCheck = computed(
  () => form.value.monitoringType === DSAttendanceMonitoringType.FOLHA
)

const stepTitle = computed(() => {
  switch (currentStep.value) {
    case STEP.FORM:
      return 'Upload de Registro de Plantão'
    case STEP.FILE:
      return 'Selecione o Arquivo'
    case STEP.PREVIEW:
      return 'Confirme o Arquivo'
    case STEP.CHECKLIST:
      return 'Antes de enviar, confira a folha'
    default:
      return 'Avisos da folha enviada'
  }
})

const mainButtonText = computed(() => {
  if (currentStep.value === STEP.CHECKLIST) return 'Enviar assim mesmo'
  if (currentStep.value === STEP.WARNINGS) return 'Concluir'
  return currentStep.value < STEP.PREVIEW ? 'Próximo' : 'Salvar'
})

const backButtonText = computed(() => {
  if (currentStep.value === STEP.CHECKLIST) return 'Voltar e corrigir'
  if (currentStep.value === STEP.WARNINGS) return 'Refazer a foto'
  return currentStep.value === STEP.FORM ? 'Cancelar' : 'Voltar'
})

const handleBackAction = () => {
  // "Refazer a foto": a folha já subiu, então o caminho é um envio novo do mesmo registro —
  // volta ao passo do arquivo mantendo o formulário (unidade, escalas, data) preenchido.
  if (currentStep.value === STEP.WARNINGS) {
    qualityWarnings.value = []
    selectedFile.value = null
    currentStep.value = STEP.FILE
    return
  }

  if (currentStep.value > STEP.FORM) {
    currentStep.value--
  } else {
    handleClose()
  }
}

const isStepFormValid = ref(false)

const toast = useToast()
const { notifyError } = useErrorToast()

const formatDateToISO = (date: Date | string | null): string => {
  if (!date) return ''
  const d = new Date(date)
  return d.toISOString().split('T')[0]
}

/** Data do registro em dd/MM/yyyy — é como o emissor a lê na folha física. */
const formattedReferenceDate = computed(() => {
  const iso = formatDateToISO(form.value.date)
  if (!iso) return ''
  const [year, month, day] = iso.split('-')
  return `${day}/${month}/${year}`
})

const healthUnitIdOrNull = computed(() => {
  const id = Number(form.value.healthUnit)
  return Number.isFinite(id) && id > 0 ? id : null
})

const selectedScheduleIdsAsNumbers = computed(() =>
  form.value.schedules.map(Number).filter((id) => Number.isFinite(id) && id > 0)
)

const buildShiftsBySchedule = (): ScheduleShiftsDto[] => {
  const selectedScheduleIds = new Set(form.value.schedules.map(Number))
  const selectedShiftIds = new Set(form.value.shifts.map(Number))

  if (!selectedShiftIds.size) {
    return Array.from(selectedScheduleIds, (scheduleId) => ({
      scheduleId,
      shiftIds: []
    }))
  }

  const grouped = new Map<number, number[]>()

  for (const shift of listShifts.value) {
    const shiftId = Number(shift.id)
    const sid = Number(shift.schedule.id)

    if (!selectedShiftIds.has(shiftId) || !selectedScheduleIds.has(sid)) continue

    const shiftIds = grouped.get(sid) ?? []
    shiftIds.push(shiftId)
    grouped.set(sid, shiftIds)
  }

  // Assume que todas as escalas selecionadas têm turnos carregados no listShifts.
  // Isso é garantido pelo fluxo normal (fetchShifts carrega turnos das escalas selecionadas),
  // mas verificação defensiva avisa se uma escala selecionada está faltando no store (inconsistência de dados).
  const foundScheduleIds = new Set(grouped.keys())
  const missingSchedules = Array.from(selectedScheduleIds).filter((id) => !foundScheduleIds.has(id))
  if (missingSchedules.length > 0) {
    console.debug(
      `[AttendanceUpload] Escalas selecionadas sem turnos no store: ${missingSchedules.join(', ')}. Isso pode indicar uma inconsistência de dados.`
    )
  }

  return Array.from(grouped, ([scheduleId, shiftIds]) => ({ scheduleId, shiftIds }))
}

/**
 * Extras do Eixo 2A. Só vão quando o passo do checklist existiu: numa modalidade sem folha,
 * um `selfCheck` vazio seria uma declaração que ninguém fez.
 */
const buildUploadExtras = (): DSAttendanceUploadExtras | undefined => {
  if (!needsSelfCheck.value) return undefined
  return {
    selfCheck: buildSelfCheckPayload(selfCheckState.value),
    expectedPages: attachedPages.value
  }
}

const uploadEvidence = async (): Promise<DSAttendanceUploadResponse> => {
  const referenceDate = formatDateToISO(form.value.date)
  const healthUnitId = Number(form.value.healthUnit)
  const schedules = buildShiftsBySchedule()

  if (form.value.monitoringType === DSAttendanceMonitoringType.EXTERNAL_APP) {
    return await Network.dsAttendance.uploadExternalEvidence(
      selectedFile.value!,
      schedules,
      referenceDate,
      healthUnitId
    )
  }

  const uploadedById = Number(form.value.uploadedBy)
  const imageState = form.value.imageState ?? ImageState.PENDING
  const extras = buildUploadExtras()

  if (form.value.monitoringType === DSAttendanceMonitoringType.NON_DAILY_SHEET) {
    return await Network.dsAttendance.uploadNonDailyEvidence(
      selectedFile.value!,
      schedules,
      referenceDate,
      healthUnitId,
      uploadedById,
      rotation.value,
      imageState,
      extras
    )
  }

  return await Network.dsAttendance.uploadEvidence(
    selectedFile.value!,
    schedules,
    referenceDate,
    healthUnitId,
    uploadedById,
    rotation.value,
    imageState,
    extras
  )
}

const finishUpload = () => {
  emit('save')
  form.value.uploadAnother ? resetModal() : handleClose()
}

const handleMainAction = async () => {
  if (currentStep.value < STEP.PREVIEW) {
    currentStep.value++
    return
  }

  // "Concluir" no painel de avisos: a folha já subiu no passo anterior.
  if (currentStep.value === STEP.WARNINGS) {
    finishUpload()
    return
  }

  if (!selectedFile.value) {
    handleClose()
    return
  }

  // Folha física ganha o passo do checklist entre a pré-visualização e o POST.
  if (currentStep.value === STEP.PREVIEW && needsSelfCheck.value) {
    currentStep.value = STEP.CHECKLIST
    return
  }

  try {
    isSaving.value = true
    const response = await uploadEvidence()
    toast.success('Upload realizado com sucesso!')
    // Eixo 2B — avisos de qualidade não bloqueiam nem desfazem: o registro está enviado, e o
    // painel só oferece reenviar uma foto melhor. Sem avisos, o modal fecha como sempre.
    const warnings = response?.qualityWarnings ?? []
    // O `save` sai uma única vez, no `finishUpload` do "Concluir" — emitir aqui recarregava a
    // lista por baixo do painel de avisos que o emissor ainda está lendo.
    if (warnings.length > 0) {
      qualityWarnings.value = warnings
      currentStep.value = STEP.WARNINGS
      return
    }
    finishUpload()
  } catch (error: unknown) {
    // `error instanceof Error ? message : fallback` era um funil caseiro: repassava o
    // texto técnico de um TypeError e ignorava o tratamento de rede/timeout/5xx.
    console.error('Erro ao salvar registro:', error)
    notifyError(error, 'Não foi possível enviar o registro de presença.')
  } finally {
    isSaving.value = false
  }
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) resetModal()
  }
)
</script>

<style scoped></style>
